import { apiClient } from './client';
import type {
  ApiResponse,
  PaginatedResponse,
  RcsConfig,
  RcsTemplate,
  RcsTemplateType,
  RcsCampaign,
  RcsCampaignStatus,
  RcsDispatch,
  RcsDispatchStatus,
  RcsDispatchStats,
  RcsDispatchStatsResponse,
} from '@/types/api.types';

export const rcsApi = {
  getStatus: () => apiClient.get<ApiResponse<{ enabled: boolean }>>('/rcs/status'),

  // Config
  getConfig: () => apiClient.get<ApiResponse<RcsConfig | null>>('/rcs/config'),
  upsertConfig: (data: { agentId: string; isActive?: boolean }) =>
    apiClient.put<ApiResponse<RcsConfig>>('/rcs/config', data),
  registerWebhook: () =>
    apiClient.post<
      ApiResponse<{ subscriptionId: string | null; action: string; webhookUrl: string }>
    >('/rcs/config/register-webhook'),

  // Templates
  listTemplates: (params?: {
    page?: number;
    pageSize?: number;
    type?: RcsTemplateType;
    isActive?: boolean;
  }) => apiClient.get<PaginatedResponse<RcsTemplate>>('/rcs/templates', { params }),

  getTemplate: (id: string) => apiClient.get<ApiResponse<RcsTemplate>>(`/rcs/templates/${id}`),

  createTemplate: (data: {
    name: string;
    type: RcsTemplateType;
    content: unknown;
    zenviaTemplateId?: string;
  }) => apiClient.post<ApiResponse<RcsTemplate>>('/rcs/templates', data),

  updateTemplate: (
    id: string,
    data: {
      name?: string;
      type?: RcsTemplateType;
      content?: unknown;
      zenviaTemplateId?: string | null;
      isActive?: boolean;
    },
  ) => apiClient.patch<ApiResponse<RcsTemplate>>(`/rcs/templates/${id}`, data),

  deleteTemplate: (id: string) => apiClient.delete(`/rcs/templates/${id}`),

  syncTemplatesFromZenvia: () =>
    apiClient.post<ApiResponse<{ imported: number; updated: number; removed: number }>>(
      '/rcs/templates/sync-from-zenvia',
    ),

  // Campaigns
  listCampaigns: (params?: { page?: number; pageSize?: number; status?: RcsCampaignStatus }) =>
    apiClient.get<PaginatedResponse<RcsCampaign>>('/rcs/campaigns', { params }),

  getCampaign: (id: string) =>
    apiClient.get<ApiResponse<{ campaign: RcsCampaign; stats: RcsDispatchStats }>>(
      `/rcs/campaigns/${id}`,
    ),

  createCampaign: (data: {
    name: string;
    templateId: string;
    contactIds: string[];
    fieldMappings?: Record<string, string>;
    scheduledAt?: string;
  }) => apiClient.post<ApiResponse<RcsCampaign>>('/rcs/campaigns', data),

  startCampaign: (id: string) =>
    apiClient.post<ApiResponse<RcsCampaign>>(`/rcs/campaigns/${id}/start`),

  cancelCampaign: (id: string) =>
    apiClient.post<ApiResponse<RcsCampaign>>(`/rcs/campaigns/${id}/cancel`),

  // Dispatches
  listDispatches: (params?: {
    page?: number;
    pageSize?: number;
    status?: RcsDispatchStatus;
    campaignId?: string;
    from?: string;
    to?: string;
  }) => apiClient.get<PaginatedResponse<RcsDispatch>>('/rcs/dispatches', { params }),

  sendQuick: (data: { contactPhone: string; contactName?: string; payload: unknown }) =>
    apiClient.post<ApiResponse<RcsDispatch>>('/rcs/dispatches', data),

  retryDispatch: (id: string) => apiClient.post(`/rcs/dispatches/${id}/retry`),

  getDispatchLogs: (id: string) =>
    apiClient.get<ApiResponse<RcsDispatchLog[]>>(`/rcs/dispatches/${id}/logs`),

  getDispatchStats: (period: '7d' | '30d' | '90d' = '30d') =>
    apiClient.get<ApiResponse<RcsDispatchStatsResponse>>('/rcs/dispatches/stats', {
      params: { period },
    }),
};

export interface RcsDispatchLog {
  id: number;
  dispatchId: string;
  eventType: string;
  payload: unknown;
  createdAt: string;
}
